// src/components/ExpertiseChart.jsx
import React from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell,
  LabelList,
} from "recharts";

// Distinct categorical colors
const getExpertiseColor = (level) => {
  switch (level) {
    case "Novice":
      return "#d9d2f9"; // light purple
    case "Advanced Beginner":
      return "#b4a7d6"; // medium purple
    case "Competent":
      return "#8e7cc3"; // deeper purple
    case "Proficient":
      return "#6fa8dc"; // light blue
    case "Expert":
      return "#eeb577ff"; // orange
    case "No Data":
      return "#b7b7b7"; // gray
    default:
      return "#dcdcdc";
  }
};


// Map numeric score -> expertise level (aligned with legend ranges)
const getExpertiseLevel = (score) => {
  const numericScore = Number(score);
  if (!Number.isFinite(numericScore) || numericScore <= 0) return "No Data";
  if (numericScore >= 12) return "Expert";
  if (numericScore >= 9) return "Proficient";
  if (numericScore >= 6) return "Competent";
  if (numericScore >= 4) return "Advanced Beginner";
  if (numericScore >= 1) return "Novice";
  return "No Data";
};

// Bar labels
const ExpertiseLabel = (props) => {
  const { x, y, width, height, value, payload } = props;

  // ✅ Safe fallback: use payload.level if available, otherwise recompute
  const level = payload?.level || getExpertiseLevel(value);

  return (
    <text
      x={x + width + 5}
      y={y + height / 2}
      fill="#333"
      fontSize="11"
      fontWeight="500"
      textAnchor="start"
      dominantBaseline="middle"
    >
      {`${value} (${level})`}
    </text>
  );
};

const ExpertiseChart = ({ data = [], title = "Expertise Level" }) => {
  // Normalize all rows with consistent level + color
  const processedData = data.map((item) => {
    const rawScore = Number(item.value);
    const score = Number.isFinite(rawScore) ? rawScore : 0;
    const level = getExpertiseLevel(score);
    return {
      ...item,
      value: score,
      level,
      color: getExpertiseColor(level),
    };
  });

 const legendItems = [
  { label: "Expert", color: "#eeb577ff", range: "12-13" },
  { label: "Proficient", color: "#6fa8dc", range: "9-11" },
  { label: "Competent", color: "#8e7cc3", range: "6-8" },
  { label: "Advanced Beginner", color: "#b4a7d6", range: "4-5" },
  { label: "Novice", color: "#d9d2f9", range: "1-3" },
  { label: "No Data", color: "#b7b7b7", range: "0" },
];


  // Summary
  const totalDepartments = processedData.length;
  const levelDistribution = legendItems.map((item) => ({
    ...item,
    count: processedData.filter((dept) => dept.level === item.label).length,
    percentage:
      totalDepartments > 0
        ? Math.round(
            (processedData.filter((dept) => dept.level === item.label).length /
              totalDepartments) *
              100
          )
        : 0,
  }));

  const averageScore =
    totalDepartments > 0
      ? Math.round(
          processedData.reduce((sum, dept) => sum + dept.value, 0) /
            totalDepartments
        )
      : 0;

  const topDepartment =
    processedData.reduce(
      (prev, current) => (prev.value > current.value ? prev : current),
      processedData[0] || {}
    ) || {};

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h6 className="fw-semibold mb-0">{title}</h6>
        <small className="text-muted">Average: {averageScore}</small>
      </div>

      <ResponsiveContainer width="100%" height={280}>
        <BarChart
          layout="vertical"
          data={processedData}
          margin={{ top: 10, right: 100, left: 40, bottom: 10 }}
        >
          <XAxis type="number" domain={[0, 13]} tick={{ fontSize: 12 }} />
          <YAxis
            type="category"
            dataKey="departmentName"
            tick={{ fontSize: 12 }}
            width={100}
          />
          <Tooltip
            formatter={(value, _name, props) => [
              `Level: ${props?.payload?.level || getExpertiseLevel(value)}`,
              `Score: ${value}`,
              `Employees: ${props?.payload?.employeeCount || "N/A"}`,
            ]}
            labelFormatter={(name) => `Department: ${name}`}
            contentStyle={{
              backgroundColor: "#f8f9fa",
              border: "1px solid #dee2e6",
              borderRadius: "4px",
            }}
          />
          <Bar dataKey="value" radius={[0, 10, 10, 0]}>
            {processedData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.color} />
            ))}
            <LabelList content={<ExpertiseLabel />} />
          </Bar>
        </BarChart>
      </ResponsiveContainer>

      {/* Legend */}
      <div className="mt-3">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <small className="text-muted fw-semibold">Expertise Levels:</small>
          {topDepartment.departmentName && (
            <small className="text-success fw-semibold">
              Top: {topDepartment.departmentName} ({topDepartment.value})
            </small>
          )}
        </div>
        <div className="d-flex justify-content-center flex-wrap gap-2 mb-3">
          {legendItems.map((item, idx) => (
            <div
              key={idx}
              className="d-flex align-items-center px-2 py-1 rounded"
              style={{
                fontSize: "11px",
                backgroundColor: `${item.color}20`,
                border: `1px solid ${item.color}40`,
              }}
            >
              <div
                className="me-2"
                style={{
                  width: "12px",
                  height: "12px",
                  backgroundColor: item.color,
                  borderRadius: "2px",
                }}
              />
              <span className="text-dark">
                <strong>{item.label}</strong>
                <span className="text-muted ms-1">({item.range})</span>
              </span>
            </div>
          ))}
        </div>

        {/* Distribution Summary */}
        <div className="bg-light p-2 rounded">
          <small className="text-muted fw-semibold d-block mb-2">
            Distribution Summary:
          </small>
          <div className="row">
            {levelDistribution
              .filter((item) => item.count > 0)
              .map((item, idx) => (
                <div key={idx} className="col-6 col-md-4 mb-1">
                  <small>
                    <span style={{ color: item.color, fontWeight: "bold" }}>
                      {item.label}:
                    </span>
                    <span className="ms-1">
                      {item.count} ({item.percentage}%)
                    </span>
                  </small>
                </div>
              ))}
          </div>
        </div>
      </div>
    </>
  );
};

export default ExpertiseChart;
